import { Injectable, OnDestroy } from '@angular/core';
import { environment } from '../../environments/environment';

/**
 * Where the first page view of this session came from, remembered for the rest
 * of it. An ad lands on one page and the visitor clicks through to another;
 * the second URL is clean, but it is still the same paid click.
 */
export const TRAFFIC_SOURCE_KEY = 'sf_traffic_source';

/**
 * Longest source we keep. Matches PageEngagement.MAX_VALUE_LENGTH in
 * map-ai-backend, which drops anything over it.
 *
 * Cutting shorter than the server does would be strictly worse than sending
 * the real value: two variants of one campaign that share a long prefix
 * truncate to the same string and collapse into one row in a GROUP BY source,
 * which is the one comparison this field exists to support.
 */
const MAX_SOURCE_LENGTH = 120;

/**
 * Reads the campaign a visit arrived on out of a URL query string.
 *
 * A pure function, and exported, because it is the part with rules worth
 * testing: the service around it only decides when to call it.
 *
 * @returns `google_ads` for anything Google auto-tags, otherwise the
 *   normalised utm_source, otherwise null. Null rather than 'organic' or '':
 *   an untagged visit is one we cannot attribute, which is not the same claim
 *   as attributing it to nothing.
 */
export function trafficSourceFrom(search: string): string | null {
  const params = new URLSearchParams(search);
  // gclid on Search and Display, gbraid/wbraid on iOS campaigns since ATT.
  // Auto-tagging adds these without anyone having to remember a utm tag, so
  // they are the reliable half of this.
  if (params.get('gclid') || params.get('gbraid') || params.get('wbraid')) {
    return 'google_ads';
  }

  const utm = params.get('utm_source');
  if (!utm) return null;
  // Normalised so 'Google', 'google' and 'google ' are one value in a GROUP BY
  // rather than three.
  const normalised = utm
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_.-]+/g, '_')
    .slice(0, MAX_SOURCE_LENGTH);
  return normalised.replace(/^_+|_+$/g, '') || null;
}

/**
 * The campaign this session arrived on, if any.
 *
 * The URL wins when it has one, so a second ad click in the same session
 * re-attributes the visit rather than staying on the first campaign.
 * Otherwise the remembered one is used, which is what makes a click-through
 * keep its source - from the funnel index to an industry page, or from the
 * site to the map.
 *
 * Exported because SITE_OPENED (UserAccessService.startPageView) carries the
 * same source, on the same rule: this is not a sales-forecast-only concept,
 * just the one place the rule was first written.
 */
export function resolveTrafficSource(): string | null {
  const fromUrl =
    typeof location === 'undefined' ? null : trafficSourceFrom(location.search);

  try {
    if (fromUrl) {
      sessionStorage.setItem(TRAFFIC_SOURCE_KEY, fromUrl);
      return fromUrl;
    }
    return sessionStorage.getItem(TRAFFIC_SOURCE_KEY);
  } catch {
    // Private mode, or storage disabled. Attribution is worth less than the
    // event itself, so it still goes out without it.
    return fromUrl;
  }
}

/**
 * Remembers the campaign this session arrived on, wherever it landed.
 *
 * Called once at app start (see AppComponent), because the funnel service only
 * looks at the URL when a sales-forecast page view begins: an ad pointed at the
 * map, an explore page or the home page would lose its click id on the
 * click-through, and the visit would be counted as organic on the very page the
 * campaign paid for. The sales-forecast page also rewrites its own URL down to
 * the address (syncAddressQueryParam), which strips a `gclid` that was there -
 * capturing at start happens first.
 *
 * Idempotent, and it never clears: an untagged navigation later in the session
 * leaves an earlier campaign alone, while a fresh click id overwrites it, which
 * is the same URL-wins rule resolveSource() applies.
 *
 * @param search defaults to the current query string; passed explicitly in
 *   tests, and guarded so a non-browser context is a no-op rather than a throw.
 */
export function captureTrafficSource(
  search: string = typeof location === 'undefined' ? '' : location.search
): void {
  const source = trafficSourceFrom(search);
  if (!source) return;

  try {
    sessionStorage.setItem(TRAFFIC_SOURCE_KEY, source);
  } catch {
    // Private mode, or storage disabled. Nothing to do: resolveSource() still
    // reads the URL directly, so a direct landing is attributed either way.
  }
}

/**
 * Our own funnel log for the sales-forecast pages, POSTing to
 * `/user/metrics/events/page` (UserMetricsController in map-ai-backend).
 *
 * Deliberately duplicates the GA4 events fired from the same
 * places (GoogleAnalyticsService.logSalesForecast*). GA4 is the marketing
 * team's tool; this log is the dev team's, for joins and ad-hoc SQL against
 * the rest of `user_actions_log`, and it also captures the visitors GA4
 * loses to ad blockers. When we start paying for Google Ads clicks, "GA4 says
 * 40 sessions, our log says 95" is itself the interesting number.
 *
 * `navigator.sendBeacon`, not HttpClient: fire-and-forget, survives page
 * unload (which is exactly when the last TIME_ON_SITE threshold fires) and
 * never blocks rendering. It cannot set headers, which is why everything
 * travels as query params. The endpoint is permitAll and CSRF is disabled, so
 * this works logged out — which is most of this traffic, and all of the paid
 * traffic on day one.
 *
 * No duplicate logging: every event has exactly one call site, and this
 * service is the only thing that talks to that endpoint. Within a page view
 * each `TIME_ON_SITE` threshold fires at most once (`sent`), and a repeat
 * `startPageView` for the same slug is ignored, so an Angular re-render or a
 * query-param change does not restart the clock. Anything that still slips
 * through — a retried beacon, a page restored from the back button — carries
 * the same `pageViewId`, so a report can count distinct page views instead of
 * rows.
 */
@Injectable({ providedIn: 'root' })
export class SalesForecastFunnelService implements OnDestroy {
  /**
   * Seconds a visitor has to stay before we log another row. Must match
   * PageEngagement.TIME_ON_SITE_THRESHOLDS_SECONDS in map-ai-backend — the
   * server rejects any value not on its list.
   *
   * Dense early because that is where a paid click either bounces or does
   * not, sparse later because past a couple of minutes the exact number stops
   * changing any decision.
   */
  private static readonly TIME_ON_SITE_THRESHOLDS_SECONDS = [
    5, 10, 15, 20, 30, 60, 120, 180, 300,
  ];

  private static readonly ENDPOINT = 'user/metrics/events/page';

  /** Identifies one visit to one page, so duplicates stay collapsible in SQL. */
  private pageViewId: string | null = null;

  private page: string | null = null;

  private timers: ReturnType<typeof setTimeout>[] = [];

  /** Events already sent for the current page view. */
  private sent = new Set<string>();

  /**
   * Starts the clock for a page. Idempotent per page: calling it again for
   * the same slug does nothing, so a component that re-runs ngOnInit cannot
   * double count.
   */
  public startPageView(page: string): void {
    if (this.page === page) return;
    this.reset();
    this.page = page;
    this.pageViewId = this.newPageViewId();
    // The funnel's denominator, and the only event that is a plain page view.
    // Every later rate divides by this: before it existed the nearest stand-in
    // was the 5s TIME_ON_SITE bucket, which drops every visitor who left
    // faster than that - on paid traffic those are the bounces the campaign is
    // judged on, so every rate computed against it flattered itself.
    //
    // Also the only event that carries the traffic source. Everything later
    // joins to this row by pageViewId, so repeating it would be noise in a
    // table that is already write-heavy.
    const source = resolveTrafficSource();
    this.send('PAGE_OPENED', source ? { source } : {});
    for (const seconds of SalesForecastFunnelService.TIME_ON_SITE_THRESHOLDS_SECONDS) {
      this.timers.push(
        setTimeout(() => this.send('TIME_ON_SITE', { seconds }), seconds * 1000)
      );
    }
  }

  /**
   * The page view the purchase events should be attributed to, or null when no
   * page view is running. Handed to the checkout call so the server-written
   * half of the funnel (checkout created, paid) carries the same keys as the
   * browser-written half and the whole path is one group by `pageViewId`.
   */
  public currentPageView(): { page: string; pageViewId: string } | null {
    return this.page && this.pageViewId
      ? { page: this.page, pageViewId: this.pageViewId }
      : null;
  }

  /**
   * The first real intent signal in the funnel — everything before it is a
   * page view. Fired once per page view: a visitor who tries five addresses
   * is one interested visitor, not five.
   */
  public addressSearched(): void {
    this.send('ADDRESS_SEARCHED', {});
  }

  /**
   * Something behind the paywall was clicked — a locked card, the upsell
   * block, an upgrade prompt.
   *
   * @param placement which paywall it was, so the report can tell the upsell
   *   block from the locked cards. Not deduplicated: two different placements
   *   clicked in one visit are two different data points.
   */
  public subscriptionClicked(placement: string): void {
    this.send('SUBSCRIPTION_CLICKED', { placement });
  }

  /**
   * An already-subscribed visitor clicked toward ordering the separate,
   * one-time paid report (e.g. the top-nav CTA) — distinct from
   * subscriptionClicked() above, which is a subscription-upgrade signal and
   * would mislabel this action if reused for it.
   */
  public reportIntentClicked(placement: string): void {
    this.send('REPORT_INTENT_CLICKED', { placement });
  }

  /**
   * Stops the clock. The host component must call this on destroy: this is a
   * root-provided service, so without it the timers outlive the page and
   * "time on site" would keep counting a page the visitor has left.
   */
  public endPageView(): void {
    this.reset();
  }

  public ngOnDestroy(): void {
    this.reset();
  }

  private send(
    event: string,
    extra: { seconds?: number; placement?: string; source?: string }
  ): void {
    if (!this.page || !this.pageViewId) return;

    // One row per distinct thing that happened in this page view: each
    // TIME_ON_SITE threshold once, each paywall placement once, and
    // ADDRESS_SEARCHED once no matter how many addresses were tried (a
    // visitor who tries five is one interested visitor, not five).
    const key = `${event}:${extra.seconds ?? extra.placement ?? ''}`;
    if (this.sent.has(key)) return;
    this.sent.add(key);

    // Old browsers without sendBeacon just do not report. Not worth an
    // HttpClient fallback: a beacon during unload is the whole point, and
    // those browsers are a rounding error next to the bots we already drop.
    if (typeof navigator === 'undefined' || !navigator.sendBeacon) return;

    let url =
      `${environment.apiUrl}${SalesForecastFunnelService.ENDPOINT}` +
      `?event=${encodeURIComponent(event)}` +
      `&page=${encodeURIComponent(this.page)}` +
      `&pageViewId=${encodeURIComponent(this.pageViewId)}`;
    if (extra.seconds !== undefined) {
      url += `&seconds=${extra.seconds}`;
    }
    if (extra.placement !== undefined) {
      url += `&placement=${encodeURIComponent(extra.placement)}`;
    }
    if (extra.source !== undefined) {
      url += `&source=${encodeURIComponent(extra.source)}`;
    }

    try {
      navigator.sendBeacon(url);
    } catch {
      // Analytics must never break a page that exists to convert.
    }
  }

  private reset(): void {
    this.timers.forEach(clearTimeout);
    this.timers = [];
    this.sent.clear();
    this.page = null;
    this.pageViewId = null;
  }

  private newPageViewId(): string {
    // crypto.randomUUID needs a secure context; local http dev is not one.
    return typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }
}
